import * as React from "react";
import { connect } from "react-redux";
import { withRouter, RouteComponentProps } from "react-router-dom";
import { Flex } from "@rebass/grid";
import * as R from "ramda";

import { ClientDimension } from "../../../types/models";
import { ThunkDispatch } from "../../../types/redux";
import { RootState } from "../../../store/reducer";

import { clientDimensionActions } from "../../../store/modules/client_dimensions";
import DimensionCreator from "./DimensionCreator";
import DimensionColumns from "./DimensionColumns";
import {
  ClientDimensionColumn,
  DEFAULT_CLIENT_DIMENSION_NAME,
} from "./ClientDimensionColumn";
import ClientMasterConfigSidebar from "./ClientMasterConfigSidebar";
import { getClientAttributesData } from "src/store/modules/client_attributes";
import api from "src/services/api";
import Toaster from "src/components/molecules/Toaster";
import { Intent } from "@blueprintjs/core";

interface IProps extends RouteComponentProps<any> {
  dispatch: ThunkDispatch;
  clientDimensions: Array<Partial<ClientDimension>>;
  isEditingView: boolean;
  clientId: number;
}

class ClientInitialDimensionsEditor extends React.Component<IProps, any> {
  constructor(props: IProps) {
    super(props);
    this.isValidDimensionName = this.isValidDimensionName.bind(this);

    this.state = {
      isReordering: false,
      showSidebar: false,
      sideBarDimension: null,
      clientAttributes: [],
      totalAttributesList: [],
      sideBarType: null,
      selectedAttributes: [],
      isFetchAttributesLoading: false,
      parentDimName: null
    };
  }

  componentDidMount(): void {
    // Fetch client attributes data
    const { clientId, dispatch } = this.props;
    if (clientId) {
      dispatch(getClientAttributesData(clientId) as any)
        .then((clientAttributes: any[]) => {
          this.setState({ clientAttributes });
        })
        .catch((err: any) => {
          console.error("Error fetching client attributes data:", err);
        });
    }
  }

  componentDidUpdate(prevProps: IProps, prevState: any) {
    if (prevState.sideBarType !== this.state.sideBarType) {
      this.handleSideBarTypeChange();
    }
  }

  private handleSideBarTypeChange = () => {
    const { sideBarType } = this.state;

    // Add your custom logic here based on sideBarType
    switch (sideBarType) {
      case "DIMENSION":
        console.log("Handling dimension sidebar type");
        break;
      case "DIMENSION_INSTANCE":
        console.log("Handling attribute sidebar type");
        break;
      default:
        break;
    }
  };

  private toggleSideBar = (dimension: any, type: string) => {
    const { showSidebar, sideBarDimension } = this.state;

    // Check if clicking on the same dimension
    const isSameDimension =
      showSidebar && sideBarDimension && sideBarDimension.id === dimension.id;

    // If same dimension is clicked, toggle close. If different dimension, keep open and update
    const newShowSidebar = isSameDimension ? false : true;

    this.setState({
      showSidebar: newShowSidebar,
      sideBarDimension: dimension,
      sideBarType: type,
    });

    if (newShowSidebar) {
      this.fetchAttributeMappings(dimension, type);
    }
  };

  private closeSideBar = () => {
    this.setState({ showSidebar: false });
  };

  private fetchAttributeMappings = (dimension: any, type: string) => {
    const { clientId } = this.props;

    let dimensionId = dimension.id;
    if (type === "DIMENSION_INSTANCE") {
      const instance = this.props.clientDimensions.filter((d: any) =>
        d.instances?.find(
          (i: any) => i.id === dimension.id && i.Name === dimension.Name
        )
      );
      if (instance && instance.length > 0) {
        dimensionId = instance[0].id; // Assuming each instance has a reference to its parent dimension ID
      }
    }
    this.setState({ isFetchAttributesLoading: true });

    api
      .getAttributeMappings(clientId, dimension.ClientDimensionID || dimensionId)
      .then((attributesMapping) => {
        if (type === "DIMENSION") {
          this.setState({
            selectedAttributes: attributesMapping,
            isFetchAttributesLoading: false,
            totalAttributesList: this.state.clientAttributes,
          });
        } else if (type === "DIMENSION_INSTANCE") {
          const totalAttributesList = this.state.clientAttributes
            .filter((attr: any) =>
              attributesMapping.some(
                (mapping: any) => mapping.ClientAttributeID === attr.id
              )
            )
            .map(
              (i: any) =>
                i.instances.map((ins: any) => ({
                  ...ins,
                  parentAttr: i.Name,
                })) || []
            )
            .flat();

          this.setState({
            totalAttributesList,
            // selectedAttributes: totalAttributesList,
            isFetchAttributesLoading: false,
            parentDimName: this.props.clientDimensions.find(i => i.id ===
               (dimension.ClientDimensionID || dimensionId))?.Name,
          });

          this.fetchInstanceAttributeMappings(dimension.ClientDimensionID || dimensionId, dimension.id);
        }
      })
      .catch((error) => {
        console.error("Error fetching attribute mappings:", error);
        this.setState({ isFetchAttributesLoading: false });
      });
  };

  private fetchInstanceAttributeMappings = (
    dimensionId: number,
    dimensionInstanceId: any
  ) => {
    const { clientId } = this.props;
    this.setState({ isFetchAttributesLoading: true });
    api
      .getInstanceAttributeMappings(clientId, dimensionId, dimensionInstanceId)
      .then((selectedAttributes) => {
        selectedAttributes = selectedAttributes.map((attr: any) => ({
          ...attr,
          ClientAttributeID: attr.ClientAttributeInstanceID,
        }));
        this.setState({ selectedAttributes, isFetchAttributesLoading: false });
      })
      .catch((error) => {
        console.error("Error fetching instance attribute mappings:", error);
        this.setState({ isFetchAttributesLoading: false });
      });
  };

  public handleAttributeMapping = (
    selectedAttributes: any[],
    selectedItemsByAttribute: any
  ) => {
    const { sideBarDimension, sideBarType } = this.state;
    const { clientId } = this.props;

    if (sideBarDimension && sideBarType === "DIMENSION") {
      const clientAttributeIds = selectedAttributes.map(
        (attr: any) => attr.id || attr.ClientAttributeID
      );
      api
        .saveAttributeMappings(
          clientId,
          sideBarDimension.id,
          clientAttributeIds
        )
        .then(() => {
          Toaster.show({
            message: "Attribute mappings saved successfully",
            intent: Intent.SUCCESS,
          });
        })
        .catch(() => {
          Toaster.show({
            message: "Error saving attribute mappings",
            intent: Intent.DANGER,
          });
        });
    } else if (sideBarDimension && sideBarType === "DIMENSION_INSTANCE") {
      const instance = this.props.clientDimensions.filter((d: any) =>
        d.instances?.find(
          (i: any) =>
            i.id === sideBarDimension.id && i.Name === sideBarDimension.Name
        )
      );
      const clientAttributeIds = Object.values(selectedItemsByAttribute).map(
        (attr: any) => Number(attr.value)
      );
      if (instance && instance.length > 0) {
        const dimensionId = instance[0].id; // Assuming each instance has a reference to its parent dimension ID
        if (dimensionId) {
          api
            .saveInstanceAttributeMappings(
              clientId,
              dimensionId,
              sideBarDimension.id,
              clientAttributeIds
            )
            .then(() => {
              Toaster.show({
                message: "Attribute instance mappings saved successfully",
                intent: Intent.SUCCESS,
              });
            })
            .catch(() => {
              Toaster.show({
                message: "Error saving attribute instance mappings",
                intent: Intent.DANGER,
              });
            });
        }
      }
    }
  };

  public render() {
    const isAddingDimension = this.props.clientDimensions.some(
      (d) => DEFAULT_CLIENT_DIMENSION_NAME === d.Name
    );

    const dimColumnStyle: any = this.state.showSidebar
      ? { maxWidth: "calc(100vw)", overflowY: "auto", width: "100%" }
      : { width: "100%" };

    return (
      <Flex style={{ gap: "10px", maxHeight: "calc(100vh - 132px)", overflowY: "auto" }}>
        <div style={dimColumnStyle}>
          <DimensionColumns noTabBarHeight>
            {(this.props.clientDimensions ?? []).map(
              (dimension: any, index: number) => (
                <div key={index}>
                  <ClientDimensionColumn
                    key={`${dimension.id}-${dimension.Name}`}
                    dimension={dimension}
                    validateName={this.isValidDimensionName}
                    isEditingView={this.props.isEditingView}
                    dispatch={this.props.dispatch}
                    clientId={this.props.clientId}
                    toggleSideBar={this.toggleSideBar}
                  />
                </div>
              )
            )}
            <div
              style={{
                maxHeight: "200px",
                minHeight: "200px",
                marginTop: "15px",
              }}
            >
              <DimensionCreator
                onClick={this.createDimension}
                disabled={isAddingDimension}
              />
            </div>
          </DimensionColumns>
        </div>
        {this.state.showSidebar && (
          <ClientMasterConfigSidebar
            key={this.state.sideBarDimension?.id}
            sideBarDimension={this.state.sideBarDimension}
            attributes={this.state.totalAttributesList}
            selectedAttributes={this.state.selectedAttributes}
            handleSubmit={this.handleAttributeMapping}
            isFetchAttributesLoading={this.state.isFetchAttributesLoading}
            sideBarType={this.state.sideBarType}
            onClose={this.closeSideBar}
            parentDimName={this.state.parentDimName}
          ></ClientMasterConfigSidebar>
        )}
      </Flex>
    );
  }

  private isValidDimensionName = (newName: string): boolean => {
    return !(this.props.clientDimensions ?? []).some(
      (dim: Partial<ClientDimension>) => {
        return (
          R.toLower(dim.Name || DEFAULT_CLIENT_DIMENSION_NAME) ===
          R.toLower(newName)
        );
      }
    );
  };
  private createDimension = () => {
    this.props.dispatch(
      clientDimensionActions.addClientDimension({
        Name: DEFAULT_CLIENT_DIMENSION_NAME,
      })
    );
  };
}
const mapStateToProps = (state: RootState) => ({
  clientDimensions: state.clientDimensions,
});
export default withRouter(
  connect(mapStateToProps)(ClientInitialDimensionsEditor)
);
